function _createForOfIteratorHelper(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

/**
 * @license
 * Copyright 2021 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import { backend_util, Einsum, util } from '@tensorflow/tfjs-core';
import { multiply } from './Multiply';
import { reshape } from './Reshape';
import { sum } from './Sum';
import { transpose } from './Transpose';
export function einsum(args) {
  var inputs = args.inputs,
      backend = args.backend,
      attrs = args.attrs;
  var equation = attrs.equation;
  var tensors = inputs;

  var _backend_util$decodeE = backend_util.decodeEinsumEquation(equation, tensors.length),
      allDims = _backend_util$decodeE.allDims,
      summedDims = _backend_util$decodeE.summedDims,
      idDims = _backend_util$decodeE.idDims;

  backend_util.checkEinsumDimSizes(allDims.length, idDims, tensors);

  var _backend_util$getEins = backend_util.getEinsumComputePath(summedDims, idDims),
      path = _backend_util$getEins.path,
      steps = _backend_util$getEins.steps;

  var nSteps = steps.length;
  var out = null;
  var numDimsRemaining = allDims.length;
  var tensorsToDispose = [];

  for (var i = 0; i < nSteps; ++i) {
    var _iterator = _createForOfIteratorHelper(steps[i]),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var idTerm = _step.value;

        var _backend_util$getEins2 = backend_util.getEinsumPermutation(numDimsRemaining, idDims[idTerm]),
            perm = _backend_util$getEins2.permutationIndices,
            dimsToExpand = _backend_util$getEins2.expandDims;

        var x = void 0;

        if (backend_util.isIdentityPermutation(perm)) {
          x = tensors[idTerm];
        } else {
          x = transpose({
            inputs: {
              x: tensors[idTerm]
            },
            backend: backend,
            attrs: {
              perm: perm
            }
          });
          tensorsToDispose.push(x);
        }

        var targetShape = x.shape.slice();

        for (var k = 0; k < dimsToExpand.length; ++k) {
          targetShape.splice(dimsToExpand[k], 0, 1);
        }

        if (!util.arraysEqual(x.shape, targetShape)) {
          x = reshape({
            inputs: {
              x: x
            },
            backend: backend,
            attrs: {
              shape: targetShape
            }
          });
          tensorsToDispose.push(x);
        }

        if (out === null) {
          out = x;
        } else {
          // tslint:disable-next-line: no-unnecessary-type-assertion
          out = multiply({
            inputs: {
              a: x,
              b: out
            },
            backend: backend
          });
          tensorsToDispose.push(out);
        }
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    if (i < nSteps - 1) {
      if (path[i] >= 0) {
        out = sum({
          inputs: {
            x: out
          },
          backend: backend,
          attrs: {
            axis: path[i] - (allDims.length - numDimsRemaining),
            keepDims: false
          }
        });
        tensorsToDispose.push(out);
      }

      numDimsRemaining--;
    }
  } // Clean up intermediate tensors.


  for (var _i = 0, _tensorsToDispose = tensorsToDispose; _i < _tensorsToDispose.length; _i++) {
    var tensorInfo = _tensorsToDispose[_i];

    if (tensorInfo === out) {
      continue;
    }

    backend.disposeIntermediateTensorInfo(tensorInfo);
  }

  return out;
}
export var einsumConfig = {
  kernelName: Einsum,
  backendName: 'cpu',
  kernelFunc: einsum
};